import { useState } from "react";
import { CitationView } from "../../../citations/components/CitationView";
import type { ChunkCitation, OrgKey, TraceCitation } from "../../types";
import styles from "./TraceCitations.module.css";

export interface TraceCitationsProps {
  citations?: TraceCitation[];
  chunkCitations?: ChunkCitation[];
  isLoadingChunkCitations?: boolean;
  knowledgeOrgs?: OrgKey[];
}

function TraceCitationItem({ citation }: { citation: TraceCitation }) {
  const [expanded, setExpanded] = useState(false);
  const isUnresolved = citation.doc_processing_status === "failed";
  const title = citation.doc_name ?? citation.doc_id;

  return (
    <li className={styles.citationItem} data-testid={`citation-${citation.doc_id}`}>
      <button
        type="button"
        className={styles.citationToggle}
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
      >
        <span className={styles.docName}>{title}</span>
        <span className={styles.toggleLabel}>
          {expanded ? "Hide details" : "Show details"}
        </span>
      </button>
      {expanded && (
        <div className={styles.citationDetails}>
          <p className={styles.meta}>
            {citation.org_category} / {citation.org_id}
          </p>
          <p className={styles.meta}>Document ID: {citation.doc_id}</p>
          {citation.chunk_index != null && (
            <p className={styles.meta}>Chunk index: {citation.chunk_index}</p>
          )}
          {citation.doc_processing_status && (
            <p
              className={`${styles.meta} ${isUnresolved ? styles.statusUnresolved : ""}`}
            >
              Status: {citation.doc_processing_status}
            </p>
          )}
          <p className={styles.metaHint}>
            Chunk text is unavailable for this trace projection.
          </p>
        </div>
      )}
    </li>
  );
}

function KnowledgeOrgItem({ org }: { org: OrgKey }) {
  return (
    <li className={styles.orgItem} data-testid={`knowledge-org-${org.id}`}>
      <p className={styles.docName}>{org.category}</p>
      <p className={styles.meta}>Org ID: {org.id}</p>
    </li>
  );
}

function AssistantCitationsSection({
  citations,
  chunkCitations,
  isLoadingChunkCitations,
}: {
  citations: TraceCitation[];
  chunkCitations?: ChunkCitation[];
  isLoadingChunkCitations?: boolean;
}) {
  if (isLoadingChunkCitations) {
    return <p className={styles.empty}>Loading citation chunks…</p>;
  }

  if (chunkCitations?.length) {
    return (
      <div className={styles.expandableCitations} data-testid="chunk-citations-view">
        <CitationView citations={chunkCitations} />
      </div>
    );
  }

  if (citations.length > 0) {
    return (
      <ul className={styles.list}>
        {citations.map((citation) => (
          <TraceCitationItem
            key={`${citation.doc_id}:${citation.chunk_index ?? "na"}`}
            citation={citation}
          />
        ))}
      </ul>
    );
  }

  return <p className={styles.empty}>No citations on the selected answer.</p>;
}

export function TraceCitations({
  citations = [],
  chunkCitations,
  isLoadingChunkCitations = false,
  knowledgeOrgs = [],
}: TraceCitationsProps) {
  const hasCitations = citations.length > 0 || (chunkCitations?.length ?? 0) > 0;
  const hasKnowledgeOrgs = knowledgeOrgs.length > 0;

  if (!hasCitations && !hasKnowledgeOrgs && !isLoadingChunkCitations) {
    return (
      <section className={styles.container} aria-label="Citations and knowledge orgs">
        <h2 className={styles.heading}>Citations &amp; knowledge</h2>
        <p className={styles.empty}>No citations or knowledge orgs for this trace.</p>
      </section>
    );
  }

  return (
    <section className={styles.container} aria-label="Citations and knowledge orgs">
      <h2 className={styles.heading}>Citations &amp; knowledge</h2>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Assistant citations</h3>
        <AssistantCitationsSection
          citations={citations}
          chunkCitations={chunkCitations}
          isLoadingChunkCitations={isLoadingChunkCitations}
        />
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Knowledge orgs</h3>
        {hasKnowledgeOrgs ? (
          <ul className={styles.list}>
            {knowledgeOrgs.map((org) => (
              <KnowledgeOrgItem key={`${org.category}:${org.id}`} org={org} />
            ))}
          </ul>
        ) : (
          <p className={styles.empty}>No knowledge orgs attached to this thread.</p>
        )}
      </div>
    </section>
  );
}
